/**
 * StarRating — Inline 5-star display for article card action bars.
 *
 * Renders sparkle-style stars that show the aggregate average rating.
 * On click: dispatches openRatingModal (auth) or showRatingAuthGate (guest).
 * All overlays (modal, auth-gate, nudge) are rendered by a SINGLE
 * RatingModalProvider mounted at the app level — this component dispatches only.
 * Uses <button> (not <a>) because it's nested inside ArticleLinkWrapper's <a>.
 */
import React, { useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
    selectArticleRating,
    selectUserRating,
    selectIsSubmitting,
    openRatingModal,
    showRatingAuthGate,
} from '../../slices/ratingsSlice.mjs';

// ── SVG Sparkle Star ────────────────────────────────────────────────────────────

const StarIcon = ({ filled = false, halfFilled = false, size = 16 }) => (
    <svg
        className={`star-rating-icon ${filled ? 'filled' : ''} ${halfFilled ? 'half-filled' : ''}`}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        aria-hidden="true"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={filled ? 0 : 1.5}
            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.562.562 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
        />
    </svg>
);

// ── Main Component ──────────────────────────────────────────────────────────────

const StarRating = ({ articleId, variant = 'card' }) => {
    const dispatch = useDispatch();
    const { isAuthenticated } = useSelector((state) => state.auth);

    const ratingData = useSelector((state) => selectArticleRating(state, articleId), shallowEqual);
    const userRating = useSelector((state) => selectUserRating(state, articleId));
    const isSubmitting = useSelector((state) => selectIsSubmitting(state, articleId));

    const [hoverIndex, setHoverIndex] = useState(-1);

    // Ref tracks hover position for the click handler — state is stale
    // in the useCallback closure, and onMouseLeave resets it before onClick fires.
    const hoverIndexRef = useRef(-1);

    const { averageRating, totalRatings } = ratingData;

    // ── Click handler — dispatch to Redux, no local portal state ──
    const handleClick = useCallback(
        (e) => {
            e.stopPropagation();
            e.preventDefault();

            if (!isAuthenticated) {
                dispatch(showRatingAuthGate({ articleId }));
                return;
            }

            const capturedHover = hoverIndexRef.current;
            dispatch(openRatingModal({
                articleId,
                initialStars: capturedHover >= 0 ? capturedHover + 1 : 0,
            }));
        },
        [isAuthenticated, articleId, dispatch]
    );

    // ── Hover handlers (desktop only) ──
    const handleMouseEnter = useCallback((idx) => {
        if (window.matchMedia('(hover: hover)').matches) {
            hoverIndexRef.current = idx;
            setHoverIndex(idx);
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        // Keep ref at last hovered position — click handler reads it
        setHoverIndex(-1);
    }, []);

    // ── Render stars ──
    const renderStars = () => {
        const displayRating = hoverIndex >= 0 ? hoverIndex + 1 : averageRating;

        return Array.from({ length: 5 }, (_, i) => {
            const starNumber = i + 1;
            const filled = starNumber <= Math.floor(displayRating);
            const halfFilled =
                !filled &&
                starNumber === Math.ceil(displayRating) &&
                displayRating % 1 >= 0.25;

            return (
                <span
                    key={i}
                    className="star-rating-star"
                    onMouseEnter={() => handleMouseEnter(i)}
                    onMouseLeave={handleMouseLeave}
                >
                    <StarIcon
                        filled={filled || halfFilled}
                        halfFilled={halfFilled}
                        size={variant === 'card' ? 14 : 18}
                    />
                </span>
            );
        });
    };

    return (
        <button
            className={`star-rating star-rating--${variant}`}
            onClick={handleClick}
            disabled={isSubmitting}
            data-rating-button
            title={
                isAuthenticated
                    ? userRating
                        ? `Your rating: ${userRating.rating}★ — Click to edit`
                        : 'Rate this article'
                    : 'Log in to rate articles'
            }
            aria-label={`Rating: ${averageRating} out of 5, ${totalRatings} total ratings`}
        >
            <span className="star-rating-stars">{renderStars()}</span>
            {totalRatings > 0 && (
                <span className="star-rating-count">{totalRatings}</span>
            )}
        </button>
    );
};

export default StarRating;
